import { type LoadDiscoveryRecommendations, useApp, useUserSettings } from "@next-platform/client-common-core";
import { RECOMBEE_REQUEST_TIMEOUT_LOCAL_STORAGE } from "@next-platform/client-common-core/infra/constants";
import basicAppConfig from "@next-platform/client-common-core/infra/constants/basicAppConfig";
import { useCallback } from "react";

import {
  DEFAULT_HOMEPAGE_RECOMMENDATIONS_NUM,
  HOMEPAGE_RECOMMENDATIONS_LOCAL_STORAGE_KEY_PREFIX,
  NSFW_LOCAL_STORAGE_KEY,
  SFW_LOCAL_STORAGE_KEY,
} from "@/constants";
import { useRecombee } from "@/hooks";
import useDiscoverNsfwSelection from "@/pages/Discover/hooks/useDiscoverNsfwSelection";

const EMPTY_RECOMMENDATIONS = { recomms: [] } as const;

/**
 * Feeds the Discovery "For You" row from the app's existing Recombee request.
 *
 * It deliberately reuses Home's cache entry and key: one Recombee request
 * serves both surfaces per cache window, and a "less like this" purge is
 * reflected on both. The abort signal is forwarded so a viewer-id change
 * cannot let a late guest result replace that shared entry. Filtering,
 * credentials, and the e2e fallback all stay inside `useRecombee`.
 */
export default function useDiscoverRecommendationSource(): LoadDiscoveryRecommendations {
  const { getCachedRecommendations } = useRecombee();
  const { blockedUsers, selectedTags } = useUserSettings();
  const { recombeeRecommendationsConfig } = useApp();
  const isNsfw = useDiscoverNsfwSelection();
  const { homepageScenarioName: scenario, homepageRecommendationsFetchCount } = recombeeRecommendationsConfig ?? {};

  return useCallback(
    async ({ userId, signal }) => {
      if (!scenario) {
        return EMPTY_RECOMMENDATIONS;
      }

      const nsfwKey = isNsfw ? NSFW_LOCAL_STORAGE_KEY : SFW_LOCAL_STORAGE_KEY;
      const recommendations = await getCachedRecommendations({
        storageKey: `${HOMEPAGE_RECOMMENDATIONS_LOCAL_STORAGE_KEY_PREFIX}_${nsfwKey}`,
        timeOut: Number(RECOMBEE_REQUEST_TIMEOUT_LOCAL_STORAGE),
        userId,
        scenario,
        recommendationsCount: homepageRecommendationsFetchCount ?? DEFAULT_HOMEPAGE_RECOMMENDATIONS_NUM,
        blockedUsers,
        selectedTags,
        nsfwToggle: isNsfw,
        applicationId: basicAppConfig.applicationId,
        signal,
      });

      return recommendations ?? EMPTY_RECOMMENDATIONS;
    },
    [getCachedRecommendations, scenario, homepageRecommendationsFetchCount, blockedUsers, selectedTags, isNsfw],
  );
}
